import { linesOfRule } from './activeRules.ts'
import { subjectOf } from './rulePlacement.ts'
import { RuleSubject } from './vocabulary.ts'
import type { Finding } from './api.ts'
import { labelReason, labelSides } from './labelFindings.ts'
import { registrationReason } from './registrationReason.ts'
import { titleCaseName } from './titleCase.ts'

/**
 * Turning an verification into the screen V1 shows.
 *
 * V1's detail is **item-centred**: the document's own checks at the top, then
 * one preparation at a time -- "2) Bio Serum 20% com Poretect…" -- with its
 * checks underneath as two columns, RECEITA against NO SISTEMA. V2's screen
 * was check-centred, a flat list of every finding in the verification grouped by
 * check name, which is a different screen answering a different question.
 *
 * The grouping needs no API change and no migration. Every preparation-scoped
 * finding already carries `evidence.preparationId` and every ingredient-scoped one
 * `evidence.ingredientId`, and the verification already returns the whole prescription
 * and quotation. The ids stay *inside* evidence deliberately: the fingerprint
 * hashes evidence, so promoting them to columns would be free while copying
 * them anywhere that changes the hash would invalidate every decision a
 * pharmacist has ever recorded.
 */

/** What the verification stores for each side. Mirrors `@rp/core`'s `Preparation`. */
export interface SideIngredient {
  id: string
  rawName: string
  /** What the ERP says the line is; only the quotation side carries it. */
  kind?: string
  /** The ERP's own letter for it, shown when `kind` is not one we translate. */
  kindCode?: string
  /** For a line an ERP association added: the line that asked for it. */
  associatedWith?: string
  /** The product keyed on this line; only the quotation side carries it. */
  erpCode?: string
  dose?: { amount: number; unit: string }
  /**
   * Where this ingredient was read from.
   *
   * On the ingredient, not on a finding. A finding cites blocks only when it
   * has something to say -- an ingredient whose dose agrees produces a finding
   * with no citation at all -- and the row still has to be able to point at
   * itself on the page. Same reason `patientBlocks` lives on the document.
   */
  nameBlocks?: string[]
  doseBlocks?: string[]
}

export interface SidePreparation {
  id: string
  ingredients: SideIngredient[]
  form?: string
  posology?: string
  quantity?: { amount: number; unit: string }
  /** The label the ERP will print; quotation side only. Mirrors `PrintedLabel`. */
  label?: PrintedLabelView
}

/** What the Rótulo panel draws: the label's lines, as printed. */
export interface PrintedLabelView {
  lines: string[]
}

export interface Side {
  patientName?: string
  prescriberName?: string
  preparations: SidePreparation[]
  /**
   * Where the name was read from.
   *
   * Carried by the document rather than by the finding, exactly as V1 carries
   * `patientRegion` and `prescriberRegion` separately from its identity rows.
   * A finding only cites blocks when it has something to say -- an agreeing
   * patient name has no regions on it -- and the guided pass still has to
   * frame the name it is asking about.
   */
  patientBlocks?: string[]
  prescriberBlocks?: string[]
}

export interface VerificationItem {
  /** 1-based, as V1 numbers them on screen. */
  readonly ordinal: number
  readonly preparationId: string
  /** What the preparation is called, for the item's heading. */
  readonly title: string
  /** The prescribed ingredients, which the ATIVOS board is a list of. */
  readonly ingredients: readonly SideIngredient[]
  readonly findings: readonly Finding[]
  readonly outstanding: number
  /**
   * The receita, when the item is a keyed one.
   *
   * Present, the ATIVOS board is drawn from the keyed ingredients with each
   * one's receita line looked up here -- name, dose and the blocks a pin
   * points at. Absent, `ingredients` are the receita's own and the board is
   * drawn from them directly, as it was before the quotation was the anchor.
   */
  readonly prescription?: Side | undefined
  /** The keyed pote's label, when the ERP had one. */
  readonly label?: PrintedLabelView | undefined
}

export interface VerificationItems {
  /** patient, prescriber, completeness -- V1's identity rows. */
  readonly document: readonly Finding[]
  readonly items: readonly VerificationItem[]
  /**
   * Findings that name a preparation or ingredient the prescription does not have.
   *
   * Never expected, always shown. An earlier system dropped items that fell
   * through its grouping and displayed a prescription that looked unread; the
   * rule inherited from `pairPreparations` is that an item which exists can never
   * vanish, and that rule is worth nothing if the screen re-breaks it.
   */
  readonly orphans: readonly Finding[]
  /**
   * Receita preparations none of whose ingredients was keyed. Each is drawn
   * as a step of its own after the keyed items, named by its ingredients,
   * because there is no keyed item for it to belong to.
   */
  readonly unquoted: readonly Finding[]
}

export function parseSide(value: unknown): Side | undefined {
  if (value === null || typeof value !== 'object') return undefined
  const side = value as Side & { selections?: unknown }
  if (!Array.isArray(side.preparations)) return undefined
  if (!Array.isArray(side.selections)) return side

  /*
   * The product keyed on each line, onto the line.
   *
   * The stored quotation keeps them apart -- `selections` beside the
   * preparations -- and the screen had only an identity finding's code to link
   * a name with, so a keyed line nothing resolved could not open its product.
   */
  const codeOf = new Map(
    side.selections.flatMap((s) => {
      const { ingredientId, erpCode } = (s ?? {}) as { ingredientId?: unknown; erpCode?: unknown }
      return typeof ingredientId === 'string' && typeof erpCode === 'string' && erpCode !== ''
        ? [[ingredientId, erpCode] as const]
        : []
    }),
  )
  return {
    ...side,
    preparations: side.preparations.map((preparation) => ({
      ...preparation,
      ingredients: preparation.ingredients.map((ingredient) => {
        const erpCode = ingredient.erpCode ?? codeOf.get(ingredient.id)
        return erpCode === undefined ? ingredient : { ...ingredient, erpCode }
      }),
    })),
  }
}

/**
 * The heading for one preparation.
 *
 * V1 titles an item by what it is made of, because "Preparação 2" tells a
 * pharmacist nothing about which one they are looking at while the prescription
 * sits next to it. Falls back to the form, and then to the ordinal, rather than
 * rendering an empty heading.
 */
/** The lines a receita could have named: not the pot, the shell or the excipient. */
export function keyedIngredients(preparation: SidePreparation): SideIngredient[] {
  return preparation.ingredients.filter((c) => c.kind === undefined || c.kind === 'active')
}

export function itemTitle(preparation: SidePreparation, ordinal: number): string {
  const names = keyedIngredients(preparation)
    .map((c) => c.rawName.trim())
    .filter((n) => n !== '')
  if (names.length > 0) return names.join(' + ')
  if (preparation.form !== undefined && preparation.form.trim() !== '') return preparation.form
  return `Preparação ${ordinal}`
}

const DOCUMENT_DIMENSIONS = new Set(['patient', 'prescriber', 'registration', 'completeness'])

/** What a não orçado says: the fact, and the two things it can mean. */
export const UNQUOTED_REASON =
  'Prescrito na receita e não encontrado no orçamento. Confirmar se o paciente não vai levar ou se faltou orçar.'

/** A receita preparation the pharmacy never keyed, in any of its ingredients. */
export function isUnquoted(finding: Finding): boolean {
  return (
    finding.check === 'completeness' &&
    (finding.evidence as { kind?: unknown } | undefined)?.kind === 'unquoted'
  )
}

/**
 * Whether a finding is about the requisition rather than one preparation.
 *
 * The check answers this for the nine comparisons, which each live at one
 * level and always have. It cannot answer it for the operational rules: twelve
 * of the fifteen are about a preparation and three are about the receita --
 * "esta receita controlada está vencida" is not a fact about pote 1, and filed
 * there a pharmacist answers it for the wrong thing.
 *
 * So the finding's own scope wins where it has one, and the check is the
 * fallback for rows written before it did.
 */
function isDocumentScoped(finding: Finding): boolean {
  if (finding.scope === 'document') return true
  if (finding.scope === 'preparation' || finding.scope === 'ingredient') return false
  return DOCUMENT_DIMENSIONS.has(finding.check)
}

/** Whether a finding still needs a person. */
export function isOutstanding(finding: Finding, answered: ReadonlySet<string>): boolean {
  return (
    (finding.disposition === 'divergence' || finding.disposition === 'undecided') &&
    !answered.has(finding.fingerprint)
  )
}

/**
 * The verification as a list of items, one per preparation the pharmacist keyed.
 *
 * The quotation is the anchor, as it was in V1: a pharmacist checks what
 * she keyed, and the receita is the evidence held against each item. So
 * `anchor` is the quotation side; findings carry the keyed item's id.
 */
export function groupByItem(
  anchor: Side | undefined,
  findings: readonly Finding[],
  answered: ReadonlySet<string> = new Set(),
  prescription?: Side | undefined,
): VerificationItems {
  const preparations = anchor?.preparations ?? []

  // Which preparation a ingredient belongs to. Ingredient findings carry only
  // the ingredient's own id, so the prescription is what relates the two.
  const preparationOfIngredient = new Map<string, string>()
  for (const preparation of preparations) {
    for (const ingredient of preparation.ingredients) {
      preparationOfIngredient.set(ingredient.id, preparation.id)
    }
  }

  const byPreparation = new Map<string, Finding[]>(preparations.map((f) => [f.id, []]))
  const document: Finding[] = []
  const unquoted: Finding[] = []
  const orphans: Finding[] = []

  for (const finding of findings) {
    if (isUnquoted(finding)) {
      unquoted.push(finding)
      continue
    }
    if (isDocumentScoped(finding)) {
      document.push(finding)
      continue
    }

    const evidence = (finding.evidence ?? {}) as Record<string, unknown>
    const ingredientId =
      typeof evidence.ingredientId === 'string' ? evidence.ingredientId : undefined
    const fromIngredient =
      ingredientId === undefined ? undefined : preparationOfIngredient.get(ingredientId)
    // The finding names its keyed item; the ingredient map is for findings
    // from before every finding did.
    const preparationId =
      (typeof evidence.preparationId === 'string' ? evidence.preparationId : undefined) ??
      fromIngredient

    const bucket = preparationId === undefined ? undefined : byPreparation.get(preparationId)
    if (bucket === undefined) orphans.push(finding)
    else bucket.push(finding)
  }

  const items = preparations.map((preparation, index): VerificationItem => {
    const own = byPreparation.get(preparation.id) ?? []
    return {
      ordinal: index + 1,
      preparationId: preparation.id,
      title: itemTitle(preparation, index + 1),
      ingredients: keyedIngredients(preparation),
      findings: own,
      outstanding: own.filter((f) => isOutstanding(f, answered)).length,
      prescription,
      label: Array.isArray(preparation.label?.lines) ? preparation.label : undefined,
    }
  })

  return { document, items, unquoted, orphans }
}

/**
 * The two sides of one check, for the RECEITA / NO SISTEMA columns.
 *
 * Read from the finding's own evidence rather than from the two documents,
 * because the evidence is what the comparison actually compared -- normalized,
 * parsed, and already in the terms the score was computed on. Rebuilding the
 * pair from the raw sides would mean the screen and the score could disagree
 * about what was compared, which is the one thing a conference of two sides
 * cannot afford.
 */
export interface CheckSides {
  readonly prescribed: string | undefined
  readonly quoted: string | undefined
}

export function checkSides(finding: Finding): CheckSides {
  const evidence = (finding.evidence ?? {}) as Record<string, unknown>

  /*
   * Identity names its sides after what it is comparing.
   *
   * `checkIdentity` records the prescription's wording and the product the
   * quotation selected, not a `prescribed`/`quoted` pair -- so reading only
   * those two keys returned nothing, and an identity row rendered as two
   * dashes. Invisible on the review screen, where ingredients are drawn from
   * the item instead; plainly wrong in the histórico, which has only the
   * finding to go on.
   */
  if (finding.check === 'identity') {
    return {
      prescribed: asText(evidence.prescribedTerm),
      quoted: asText(evidence.selectedProductName ?? evidence.selectedErpCode),
    }
  }

  // Composition compares sets, not values, so its two sides are the lists.
  if (finding.check === 'completeness') {
    const kind = asText(evidence.kind)
    const names = asList(evidence.ingredients).join(' + ')
    if (kind === 'unquoted') return { prescribed: names, quoted: undefined }
    if (kind === 'unprescribed') return { prescribed: undefined, quoted: names }
    return { prescribed: undefined, quoted: undefined }
  }
  if (finding.check === 'composition') {
    const missing = asList(evidence.missing)
    const added = asList(evidence.added)
    return {
      prescribed: missing.length === 0 ? undefined : `faltando: ${missing.join(', ')}`,
      quoted: added.length === 0 ? undefined : `a mais: ${added.join(', ')}`,
    }
  }

  /*
   * A volume says which figure was compared, and what the page said.
   *
   * A receita often states how much is made only through the regimen, so the
   * compared figure may be arithmetic we did rather than words somebody wrote.
   * Shown as a bare number beside the ERP's, a pharmacist has no way to tell
   * those two apart -- which is the same problem as two dispositions drawn
   * identically, and the reason this row names its source.
   */
  if (finding.check === 'quantity') return quantitySides(evidence)
  if (finding.check === 'form') return formSides(evidence)

  // The label's reference is the receita or the sistema, the other side the label.
  if (finding.check === 'label') return labelSides(finding)

  /*
   * The document checks name their sides differently.
   *
   * `checkPatient` and `checkPrescriber` record `prescriptionNormalized` and
   * `quotationNormalized` -- normalized, because a name comparison is what
   * they scored. Reading only `prescribed`/`quoted` found neither, so the
   * identity rows rendered an em dash on both sides of a patient whose name
   * was right there in the heading above them.
   *
   * They now also record the raw pair, which is what these prefer: normalized
   * has been through `normalizeTerm`, which strips accents as well as case, so
   * `JOÃO D'ÁVILA` reaches the screen as `joao d avila`. A finding written
   * before that change has only the normalized form, and still renders.
   */
  const prescribed = asText(evidence.prescribed ?? evidence.prescriptionNormalized)
  const quoted = asText(evidence.quoted ?? evidence.quotationNormalized)

  /*
   * Both of these rows are a person's name, and a name is presented as a name.
   * Done here rather than at the two call sites so the review screen and the
   * histórico cannot drift apart on it -- `checkSides` is the one place a
   * finding becomes two strings somebody reads.
   */
  if (finding.check === 'patient' || finding.check === 'prescriber') {
    return {
      prescribed: prescribed === undefined ? undefined : titleCaseName(prescribed),
      quoted: quoted === undefined ? undefined : titleCaseName(quoted),
    }
  }

  return { prescribed, quoted }
}

/**
 * The RECEITA side of a volume: the figure compared, and where it came from.
 *
 * The reading's own total is shown whichever figure was compared. When the two
 * disagree -- "1 frasco" on the page, 30 ml through the regimen -- both are
 * there, because the pharmacist is the one who decides which the receita meant.
 */
function quantitySides(evidence: Record<string, unknown>): CheckSides {
  const quoted = asText(evidence.quoted)
  const compared = asText(evidence.prescribed)
  const read = asText(evidence.readQuantity)
  const implied = asText(evidence.impliedQuantity)

  /*
   * Both figures in the one cell, because both came from the receita.
   *
   * A third column would be a change to every row on the screen for a case
   * that belongs to one of them -- and it would put the contradiction between
   * the receita and the sistema, which is not where it is. The prescription
   * disagrees with itself; the ERP has nothing to do with it.
   */
  if (read !== undefined && implied !== undefined && read !== implied) {
    return { prescribed: `${read} · posologia dá ${implied}`, quoted }
  }

  // Older findings carry no source at all, and read exactly as they always did.
  if (evidence.quantitySource !== 'posology' || compared === undefined) {
    return { prescribed: compared, quoted }
  }

  const from = asText(evidence.derivedFrom)
  const derived =
    from === undefined ? `${compared} (da posologia)` : `${compared} (da posologia: ${from})`
  // Nothing to add when the page said the same thing, or said nothing.
  if (read === undefined || read === compared) return { prescribed: derived, quoted }

  return { prescribed: `${derived} · na receita: ${read}`, quoted }
}

/**
 * The FORMA row, with the capsule's subtype on both sides (#401).
 *
 * The sistema's form is one word, `cápsula`, and the subtype lives on the
 * shell line three sections down -- so "cápsulas vegetais" against
 * "cápsula" looked settled while nobody could see what was keyed. The
 * sistema side now names it, as V1's enriched label did ("cápsula vegetal").
 *
 * The receita side is its own words, which already carry the subtype when
 * it was written in the form. When the form does not show what was read --
 * it was in the posology, on a "cápsula vegetal qsp" line, or spelled
 * "gastroentéricas" -- the row says what was understood, because the
 * pharmacist would otherwise see a plain "cápsula" and a divergence about a
 * word that is not there (#608: Jev reads the request, from every field).
 */
function formSides(evidence: Record<string, unknown>): CheckSides {
  const prescribed = asText(evidence.prescribed)
  const quoted = asText(evidence.quoted)
  const asked = asList(evidence.prescribedSubtype)
  const keyed = asList(evidence.quotedSubtype)

  const unseen = asked.filter((label) => !shows(prescribed, label))
  const askedElsewhere = unseen.length > 0 ? `pede cápsula ${asked.join(' ')}` : undefined
  const receita =
    askedElsewhere === undefined
      ? prescribed
      : prescribed === undefined
        ? askedElsewhere
        : `${prescribed} · ${askedElsewhere}`

  const sistema =
    keyed.length === 0 || quoted === undefined ? quoted : `${quoted} ${keyed.join(' ')}`
  return { prescribed: receita, quoted: sistema }
}

/** Whether the receita's words already show a trait's label: display only, judges nothing. */
function shows(text: string | undefined, label: string): boolean {
  const plain = (value: string) =>
    value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
  const stem = plain(label).slice(0, 5)
  return (
    text !== undefined &&
    plain(text)
      .split(/[^a-z]+/)
      .some((word) => word.startsWith(stem))
  )
}

function asList(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : []
}

/** A dose is `{amount, unit}`; a form or posology is already a string. */
function asText(value: unknown): string | undefined {
  if (value === undefined || value === null) return undefined
  if (typeof value === 'string') return value.trim() === '' ? undefined : value
  if (typeof value === 'number') return String(value)
  if (typeof value === 'object') {
    const quantity = value as { amount?: unknown; unit?: unknown }
    if (typeof quantity.amount === 'number') {
      const unit = typeof quantity.unit === 'string' ? ` ${quantity.unit}` : ''
      // The decimal comma, as everything a pharmacist reads here uses.
      return `${String(quantity.amount).replace('.', ',')}${unit}`
    }
  }
  return undefined
}

/**
 * Our four dispositions in the bands the screen paints.
 *
 * Red is a disagreement: both sides spoke and they differ. Amber is a gap:
 * one side never said -- a receita with no form word -- so nothing was
 * compared. Both need a person and both count; the colour tells the
 * pharmacist which kind of look it needs before they read a word. `info` is
 * not a discrepancy, so it stays quiet.
 */
export function columnStatusFor(disposition: string, answered = false): ColumnStatus {
  // A quiet row somebody marked anyway: the pharmacist saw what we did not.
  if (disposition !== 'divergence' && disposition !== 'undecided') {
    return answered ? 'resolved' : 'no_issues'
  }
  /*
   * Undecided is amber, not red. Nothing diverged; something could not be
   * compared -- a receita that never said the form. It still needs the
   * pharmacist's look and their answer, so it is flagged and counted, but
   * it is drawn as a question, with the reason where the pin would be.
   */
  if (disposition === 'undecided') return answered ? 'resolved' : 'warning'
  /*
   * Answered, so it stops shouting.
   *
   * The difference is still real and still worth reading -- it stays on the
   * page in full. But a board that keeps every answered row red leaves a
   * pharmacist no way to see what is left, which is the only question they
   * have once they start working through one.
   */
  return answered ? 'resolved' : 'error'
}

export type ColumnStatus = 'error' | 'warning' | 'resolved' | 'no_issues'

/**
 * The worst band present, which is the band the card as a whole takes.
 *
 * A card whose divergences have all been answered goes green; one still
 * holding an unanswered divergence stays red, however many of its rows are
 * done.
 */
export function worstStatus(
  findings: readonly Finding[],
  answered: ReadonlySet<string> = new Set(),
): ColumnStatus {
  const bands = findings.map((f) => columnStatusFor(f.disposition, answered.has(f.fingerprint)))
  if (bands.includes('error')) return 'error'
  if (bands.includes('warning')) return 'warning'
  return bands.includes('resolved') ? 'resolved' : 'no_issues'
}

/** Whether a finding, unanswered, asks a pharmacist for something. */
export function needsPerson(disposition: string): boolean {
  const band = columnStatusFor(disposition)
  return band === 'error' || band === 'warning'
}

/** Whether anything on this item is worth a pharmacist's attention. */
export function hasIssues(findings: readonly Finding[]): boolean {
  return worstStatus(findings) !== 'no_issues'
}

/**
 * The per-card summary.
 *
 * One count. It used to separate differences from "verificações não
 * avaliadas", which made a card announce two numbers a pharmacist had to
 * weigh against each other before looking at anything. A undecided row is
 * counted with the rest -- it still wants an answer.
 */
export function itemSummary(findings: readonly Finding[]): string {
  const flagged = findings.filter((f) => needsPerson(f.disposition)).length
  if (flagged === 0) return 'Sem divergências'
  return flagged === 1 ? '1 diferença' : `${flagged} diferenças`
}

/**
 * The four sections of an item card, in V1's order and under V1's labels.
 *
 * Four, not nine. V2 scores nine checks and was rendering a section for
 * each, which turned one preparation into a wall of near-identical panels.
 * V1 shows FORMA, ATIVOS, POSOLOGIA and VOLUME, and a pharmacist reads the same
 * four in the same places on every card -- a section that moves is a section
 * that gets missed.
 *
 * The checks do not disappear; they are grouped. Everything about the
 * ingredients -- whether any are missing or added, whether the right product
 * was picked, whether the dose agrees -- is one question a pharmacist asks
 * once, looking at the list, so composition, identity and dose share ATIVOS.
 */
export interface ItemSection {
  label: string
  checks: readonly string[]
  /**
   * The operational rules drawn here: the ones about this part of the
   * preparation (#549). A rule about a part no section draws falls to the
   * rules section, so it is never left out.
   */
  rules?: RuleSubject
  /**
   * Drawn only when it has something to say.
   *
   * The four comparison sections are always drawn, dash and all: a section
   * that disappeared made "nothing to say about the volume" look identical to
   * "this preparation has no volume", and a pharmacist comparing two items
   * could not tell which checks had been examined.
   *
   * The rules are the opposite case. Only failures are reported at all -- a
   * rule that passed emits nothing -- so an empty board here would not mean
   * "examined and fine", it would mean nothing whatsoever, on every
   * preparation of every verification.
   */
  onlyWhenPresent?: boolean
}

export const ITEM_SECTIONS: readonly ItemSection[] = [
  { label: 'Forma', checks: ['form'], rules: RuleSubject.Form },
  // `completeness` for the item the receita never had: it rides on the rows.
  {
    label: 'Ativos',
    checks: ['composition', 'identity', 'dose', 'completeness'],
    rules: RuleSubject.Ingredient,
  },
  { label: 'Posologia', checks: ['posology'], rules: RuleSubject.Posology },
  { label: 'Volume', checks: ['quantity'], rules: RuleSubject.Quantity },
  /*
   * Last, and after the comparison.
   *
   * A rule is a different question from a difference: the four above ask
   * whether the quotation matches the receita, and this asks whether the
   * requisition is workable at all. Reading the comparison first is the order
   * a pharmacist already works in.
   */
  { label: 'Regras operacionais', checks: ['operational'], onlyWhenPresent: true },
]

export interface VerificationCounts {
  readonly total: number
  readonly reviewed: number
  readonly flagged: number
  readonly remaining: number
}

/** V1's summary line: "2 de 7 revisados · 5 sinalizados · 5 restantes". */
export function countsFor(
  findings: readonly Finding[],
  answered: ReadonlySet<string>,
): VerificationCounts {
  const flagged = findings.filter(
    (f) => f.disposition === 'divergence' || f.disposition === 'undecided',
  )
  const reviewed = findings.filter((f) => answered.has(f.fingerprint))
  return {
    total: findings.length,
    reviewed: reviewed.length,
    flagged: flagged.length,
    remaining: flagged.filter((f) => !answered.has(f.fingerprint)).length,
  }
}

/**
 * One row of the ATIVOS board: an ingredient, both sides.
 *
 * V1's board is a list of the actives with a row each -- name and dosage on the
 * left, name and dosage on the right, tinted by what that one line concluded.
 * It is not a row per check: a pharmacist looks at the ingredient list once
 * and asks one question of it.
 *
 * `quotedName` is undefined for now and the column shows the dose alone. The
 * resolver knows which product was selected, but the identity finding records
 * only the outcome and the rival code, never the chosen product's name, so
 * there is nothing here to render. Inventing it from the rival code would name
 * the product we suspected rather than the one that was picked.
 */
export interface ActiveRow {
  /** Stable within a card, so a selection survives a re-render. */
  readonly key: string
  /** The receita's name for it; undefined for a keyed line the receita never named. */
  readonly name: string | undefined
  readonly prescribedDose: string | undefined
  readonly quotedName: string | undefined
  readonly quotedDose: string | undefined
  readonly status: ColumnStatus
  /** The blocks this one ingredient was read from. Empty is a real answer. */
  readonly regions: readonly string[]
  /**
   * The ERP's code for the product: the one identity resolved, else the one
   * keyed on the line.
   *
   * Carried so the row can link to that product's page. Absent when the line
   * carries no code -- and an absent code renders as plain text rather than as
   * a link to nowhere.
   */
  readonly quotedErpCode?: string
  /**
   * The keyed ingredient's id, on a keyed line the receita never named.
   *
   * The one row that may be an excipient the ERP typed as an ingredient, and
   * what the conferente names when saying so (ADR 0014).
   */
  readonly keyedIngredientId?: string
  /**
   * This ingredient's own findings, so the row can carry its own decision.
   *
   * Exposed because the ATIVOS rows are built here rather than from a finding
   * apiece: without them the section renders the differences and offers no way
   * to answer any of them, which is exactly what happened when the controls
   * moved onto the rows.
   */
  readonly findings: readonly Finding[]
  /**
   * The operational rules' reminders about this ativo (#549), drawn as lines
   * under the row.
   *
   * Apart from `findings` because nothing here is asked of anybody: a
   * reminder must not paint the row, stop the keyboard pass on it or stand in
   * for the row's own answer. A rule that does ask is in `findings`, and the
   * row is its apontamento.
   */
  readonly notes: readonly Finding[]
}

export interface ActivesBoard {
  readonly rows: readonly ActiveRow[]
  /** Prescribed ingredients the quotation does not have. */
  readonly missing: readonly string[]
  /** Ingredients the quotation adds. Vehicles are already excluded upstream. */
  readonly added: readonly string[]
  readonly status: ColumnStatus
}

export function activesBoard(item: VerificationItem): ActivesBoard {
  const composition = item.findings.find((f) => f.check === 'composition')
  const compositionEvidence = (composition?.evidence ?? {}) as Record<string, unknown>
  const pairs = pairedIngredients(compositionEvidence.pairs)
  const paired = pairedNames(compositionEvidence.pairs)

  const evidenceOf = (f: Finding) => (f.evidence ?? {}) as Record<string, unknown>

  /*
   * Which receita line a keyed ingredient is.
   *
   * By id when the pairing said (`prescribedId`/`quotedId` on the composition
   * pairs), by name for findings written before it did. The receita's own
   * ingredients are what carry blocks, so this is also where a pin comes from.
   */
  const prescribedIngredients = new Map(
    (item.prescription?.preparations ?? []).flatMap((f) =>
      f.ingredients.map((c) => [c.id, c] as const),
    ),
  )
  const prescribedFor = (keyed: SideIngredient): SideIngredient | undefined => {
    const byId = pairs.find((pair) => pair.quotedId === keyed.id)?.prescribedId
    if (byId !== undefined) return prescribedIngredients.get(byId)
    // The same id on both sides is the same line: a simulation, or a screen
    // drawn from one side for both.
    const same = prescribedIngredients.get(keyed.id)
    if (same !== undefined) return same
    const byName = [...paired].find(([, quoted]) => quoted === keyed.rawName)?.[0]
    return byName === undefined
      ? undefined
      : [...prescribedIngredients.values()].find((c) => c.rawName === byName)
  }

  const compositionOwn = composition === undefined ? [] : [composition]
  const compositionBand = worstStatus(compositionOwn)
  /*
   * Who owns "this keyed line is not on the receita".
   *
   * Composition, when the item paired with a receita preparation and this
   * ingredient was the one the receita lacked. Completeness, when the whole
   * item never paired -- `unprescribed`, preparation scope -- which landed in
   * the item's findings and was drawn by no section: "1 diferença" over a
   * board with nothing red on it, on orçamento 831928.
   */
  const unprescribed = item.findings.filter(
    (f) =>
      f.check === 'completeness' &&
      (f.evidence as { kind?: unknown } | undefined)?.kind === 'unprescribed',
  )
  const setOwners = [...compositionOwn, ...unprescribed]

  // Which keyed lines each operational finding is about (#549).
  const ruleLines = item.findings
    .filter((f) => f.check === 'operational' && subjectOf(f) === RuleSubject.Ingredient)
    .map((f) => ({ finding: f, lines: linesOfRule(f, item.ingredients) }))

  const rows = item.ingredients.map((ingredient, index): ActiveRow => {
    const anchored = item.prescription !== undefined
    const prescribed = anchored ? prescribedFor(ingredient) : ingredient
    /*
     * A keyed line the receita never named is the composition's "added",
     * and the composition finding is the row's own: the set is answered on
     * the line that broke it. It used to be drawn twice -- once here with
     * the keyed name on the receita side, once below as "added" with no way
     * to answer it.
     */
    const unnamed = anchored && prescribed === undefined
    /*
     * The findings that are about *this line*, kept apart from the ones it
     * merely carries.
     *
     * An unnamed row also shows the preparation's set-level findings, because
     * they have to be answerable somewhere and that row is where they are
     * offered. They are not about the row, though, and their citations are
     * every line the set is missing -- so borrowing them is how pinning
     * `AC RETINOICO` also lit up `Hyalo gel concentrado`, a different
     * ingredient two lines down (50/299927).
     */
    const mine = item.findings.filter((f) => {
      const evidence = evidenceOf(f)
      return (
        evidence.quotedIngredientId === ingredient.id ||
        (prescribed !== undefined && evidence.ingredientId === prescribed.id)
      )
    })
    // The rules about this ativo (#549): the ones that ask ride on the row
    // as its own findings, the reminders go under it.
    const rules = ruleLines.filter((one) => one.lines.has(index)).map((one) => one.finding)
    const own = [
      ...mine,
      ...rules.filter((f) => needsPerson(f.disposition)),
      ...(unnamed ? setOwners : []),
    ]
    const dose = own.find((f) => f.check === 'dose')
    const sides = dose === undefined ? undefined : checkSides(dose)
    const identity = own.find((f) => f.check === 'identity')
    // The product identity judged, else the one keyed on this line: a keyed
    // line the receita never named has no identity finding, and it is still a
    // product somebody may want to open (CELULOSIX LIB MODIFICADA, 50/298937).
    const erpCode = (identity?.evidence ?? {})['selectedErpCode'] ?? ingredient.erpCode
    return {
      ...(typeof erpCode === 'string' && erpCode !== '' ? { quotedErpCode: erpCode } : {}),
      ...(unnamed ? { keyedIngredientId: ingredient.id } : {}),
      key: `active:${ingredient.id}`,
      findings: own,
      notes: rules.filter((f) => !needsPerson(f.disposition)),
      // The receita's words where it named this ingredient; nothing for a
      // keyed line the receita never mentioned -- that absence is the point.
      name: unnamed ? undefined : (prescribed?.rawName ?? ingredient.rawName),
      prescribedDose: unnamed ? undefined : (sides?.prescribed ?? quantityText(prescribed?.dose)),
      // What scoring actually paired this with. Anchored on the keyed item
      // that is the item's own line; otherwise the name the pairing recorded.
      quotedName: anchored ? ingredient.rawName : paired.get(ingredient.rawName),
      quotedDose: sides?.quoted ?? (anchored ? quantityText(ingredient.dose) : undefined),
      status: worstStatus(own),
      /*
       * Everything on the page that is this ingredient: the receita line's
       * own blocks, plus what its *own* findings cited. A keyed line has no
       * blocks of its own -- it was typed, not read -- so without its receita
       * line a row has nothing to point at, which is a true answer.
       *
       * `mine`, not `own`: a set-level finding an unnamed row carries cites
       * every line the preparation is missing, and pointing at those from one
       * row claims they are what the row is about. The step's own highlight
       * is where a preparation-wide citation belongs.
       */
      regions: [
        ...(prescribed?.nameBlocks ?? []),
        ...(prescribed?.doseBlocks ?? []),
        ...mine.flatMap((f) => f.regions),
      ].filter((ref, i, all) => all.indexOf(ref) === i),
    }
  })

  /*
   * What the receita named and the pharmacy never keyed.
   *
   * The rows above are the keyed item's lines, so a prescribed ingredient
   * with no keyed line had no row at all once the quotation became the
   * anchor: the card said "1 diferença" over a board with nothing red on it
   * and nothing to answer. Each one is a row of its own -- the receita's
   * words on the left, a dash on the right, the receita's blocks to point
   * at -- and the composition finding rides on it.
   */
  const missingNames = asList(compositionEvidence.missing)
  const missingRows = missingNames.map((name, i): ActiveRow => {
    const line = [...prescribedIngredients.values()].find((c) => c.rawName === name)
    return {
      key: `missing:${line?.id ?? i}`,
      findings: compositionOwn,
      notes: [],
      name,
      prescribedDose: quantityText(line?.dose),
      quotedName: undefined,
      quotedDose: undefined,
      status: compositionBand,
      regions: [...(line?.nameBlocks ?? []), ...(line?.doseBlocks ?? [])],
    }
  })

  return {
    rows: [...rows, ...missingRows],
    missing: missingNames,
    added: asList(compositionEvidence.added),
    status: worstStatus(item.findings.filter((f) => ACTIVE_DIMENSIONS.has(f.check))),
  }
}

/**
 * The composition findings no row of the ativos board carries.
 *
 * The board answers the set on the line that broke it -- a receita
 * ingredient the pharmacy never keyed is a row, a keyed line the receita
 * never named is a row -- so the finding is normally on screen twice over
 * if it is also drawn on its own. What is left for a stray line is the
 * case with no row to ride on: an item with no receita side to anchor to.
 */
export function strayCompositionFindings(item: VerificationItem): readonly Finding[] {
  const carried = new Set(activesBoard(item).rows.flatMap((row) => row.findings))
  return item.findings.filter((f) => f.check === 'composition' && !carried.has(f))
}

const ACTIVE_DIMENSIONS = new Set(['composition', 'identity', 'dose', 'completeness'])

function quantityText(dose: { amount: number; unit: string } | undefined): string | undefined {
  return dose === undefined ? undefined : asText(dose)
}

/** What scoring paired with what, by prescribed name. */
function pairedNames(value: unknown): ReadonlyMap<string, string> {
  if (!Array.isArray(value)) return new Map()
  const pairs = value
    .filter(
      (entry): entry is { prescribed: string; quoted: string } =>
        entry !== null &&
        typeof entry === 'object' &&
        typeof (entry as { prescribed?: unknown }).prescribed === 'string' &&
        typeof (entry as { quoted?: unknown }).quoted === 'string',
    )
    .map((entry) => [entry.prescribed, entry.quoted] as const)
  return new Map(pairs)
}

/** The composition's pairs with their ids, where the scoring wrote them. */
function pairedIngredients(
  value: unknown,
): ReadonlyArray<{ prescribedId: string; quotedId: string }> {
  if (!Array.isArray(value)) return []
  return value.flatMap((one) => {
    if (one === null || typeof one !== 'object') return []
    const pair = one as { prescribedId?: unknown; quotedId?: unknown }
    return typeof pair.prescribedId === 'string' && typeof pair.quotedId === 'string'
      ? [{ prescribedId: pair.prescribedId, quotedId: pair.quotedId }]
      : []
  })
}

/**
 * The one sentence a finding says, wherever it is drawn.
 *
 * The row and the info cell used to choose their own source: the row printed
 * the explainer's `message`, the cell printed `undecidedReason`. So one
 * undecided dose said "A dose do ingrediente diverge: ... não foi possível
 * avaliar" in the list and "As doses não puderam ser comparadas" in the table,
 * a few centimetres apart. Two sentences about one finding is two findings as
 * far as a reader is concerned.
 *
 * The explainer's is preferred where there is one: it is about *this*
 * requisition, and `undecidedReason` can only speak in general. The
 * deterministic one is the floor -- for a finding the explainer never reached,
 * and for the class of sentence that should never have been stored.
 */
export function reasonFor(finding: Finding): string | undefined {
  // A não orçado is one fact, the same on every receita, so it has one
  // sentence. The explainer was handed an `info` and evidence with no word
  // for "not keyed", and described it as a divergence: its guard dropped some
  // (K-Ox Eyes on 299927 had nothing under it) and stored the rest saying
  // "diverge a posologia" about a product nobody quoted. Stored ones too.
  if (isUnquoted(finding)) return UNQUOTED_REASON
  // Never narrated: the label's sentences are fixed, the same on every pote.
  if (finding.check === 'label') return labelReason(finding)
  // Never narrated either (#612): which part of the registration diverged
  // or could not be verified, from the evidence.
  if (finding.check === 'registration') {
    const said = registrationReason(finding)
    if (said !== undefined) return said
  }
  const written = finding.message?.trim()
  if (written !== undefined && written !== '' && !contradictsOwnDisposition(finding, written)) {
    return written
  }
  // Only the undecided have a deterministic sentence to fall back to. An `ok`
  // with no message says nothing, which is correct: there is nothing to say.
  return finding.disposition === 'undecided' ? undecidedReason(finding) : undefined
}

/**
 * A sentence that argues with its own disposition, in the crudest possible terms.
 *
 * The real guard runs in the explainer, before anything is stored. This is the
 * second line: findings written before that guard existed are in the database
 * for good, and a message that says "diverge" on something we could not
 * compare is worse than no message at all.
 */
const CLAIMS_DIFFERENCE = new Set([
  'diverge',
  'divergem',
  'divergencia',
  'divergencias',
  'difere',
  'diferem',
  'diferenca',
  'diferencas',
])

/** `não há divergência` is a correct sentence: only an un-negated claim counts. */
const NEGATORS = new Set(['nao', 'sem', 'nenhuma', 'nenhum', 'nada'])

function contradictsOwnDisposition(finding: Finding, message: string): boolean {
  if (finding.disposition !== 'undecided') return false

  const words = message
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .split(/[^a-z]+/)
    .filter(Boolean)

  return words.some(
    (word, at) =>
      CLAIMS_DIFFERENCE.has(word) &&
      !words.slice(Math.max(0, at - 3), at).some((earlier) => NEGATORS.has(earlier)),
  )
}

/**
 * Why a comparison could not be made, in the pharmacist's words.
 *
 * The scoring's reasons are for the log. The flags on the evidence say which
 * side was silent, which is what a person wants to know.
 */
export function undecidedReason(finding: Finding): string {
  const e = (finding.evidence ?? {}) as Record<string, unknown>
  switch (finding.check) {
    case 'form':
      if (e.prescriptionHasForm === false) return 'A receita não diz a forma farmacêutica.'
      if (e.quotationHasForm === false) return 'O sistema não registrou a forma farmacêutica.'
      if (e.subtypeComparison === 'unknown') {
        const asked = asList(e.subtypeUnconfirmed).join(' ')
        return `A receita pede cápsula ${asked} e o sistema não registra esse subtipo da cápsula.`
      }
      break
    case 'posology':
      if (e.prescriptionHasPosology === false) return 'A receita não traz posologia.'
      if (e.quotationHasPosology === false) return 'O sistema não registrou a posologia.'
      break
    case 'quantity':
      if (e.reason === 'one side has no amount') return 'Um dos lados não diz a quantidade.'
      if (e.reason === 'an unrecognised unit') return 'Uma das unidades não é reconhecida.'
      if (e.reason === 'different kinds of unit')
        return 'As unidades não são comparáveis entre si (por exemplo, mg e mL).'
      break
    case 'patient':
      return 'O nome do paciente falta em um dos lados.'
    case 'prescriber':
      return 'O prescritor não pôde ser comparado: falta em um dos lados.'
    case 'registration':
      if (e.reason === 'the prescription shows no registration')
        return 'A receita não traz o registro do prescritor.'
      if (e.reason === 'the quotation records no registration')
        return 'O sistema não registrou o registro do prescritor.'
      if (e.reason === 'the numbers could not be compared')
        return 'Os números do registro não puderam ser comparados.'
      return 'Nenhum dos lados traz o registro do prescritor.'
    case 'composition':
      return 'Nenhum dos lados lista um ativo prescrito.'
    case 'dose':
      return 'As doses não puderam ser comparadas.'
    case 'identity':
      return typeof e.reason === 'string'
        ? e.reason
        : 'O produto selecionado não pôde ser conferido.'
  }
  return finding.message ?? 'Não foi possível comparar este item.'
}
